import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import AppPermissionsSwitcher from '../../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../../Components/AppPermissionsSwitcher/constants/actions'
import { setDeleteCallback } from '../../../../Components/Modals/DeleteModal/utils'
import { PrintableColumnDef } from '../../../../Components/TanstackTable'
import contacts from '../../../../api/contacts/hooks'
import { Contact } from '../../../../api/contacts/types'
import { ContactType, PermissionsProps } from '../../../../api/globalTypes'
import { MODAL_TYPES } from '../../../../common/constants'
import { contactResponseFields } from '../../../../common/response/contact'
import { showModal } from '../../../../store/modal'
import { formatDateTime, formatNumberWithTwoDecimals } from '../../../../utils/general'
import { handleActiveContactProfiles } from '../../utils'
import columnWidths from './columnWidths'

export const useTableColumns = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const location = useLocation()

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const w = useMemo(() => ({ ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }), [currentLang])

    const { mutate: deleteContact } = contacts.DeleteContact.useMutation()

    const handleDelete = useCallback(
        (id: number) => {
            const callbackId = 'delete-contact'
            const payload = {
                modalType: MODAL_TYPES.DELETE_MODAL,
                modalProps: { callbackId, id },
            }

            setDeleteCallback(callbackId, () => deleteContact({ id, contactType: ContactType.Person }))
            dispatch(showModal(payload))
        },
        [dispatch, deleteContact]
    )

    return useMemo(
        (): PrintableColumnDef<Contact>[] => [
            {
                header: t('app.common.id'),
                id: contactResponseFields.INTERNAL_APP_ID,
                accessorKey: contactResponseFields.INTERNAL_APP_ID,
                enableSorting: true,
                size: w.internalAppId,
                meta: { exportId: contactResponseFields.INTERNAL_APP_ID },
            },
            {
                header: t('app.common.contact.nameAndSurname'),
                id: 'name',
                accessorFn: (row) => row.contactPerson?.firstName,
                enableSorting: true,
                size: w.name,
                cell: ({ row }) => (
                    <>
                        {row.original?.contactPerson?.firstName} {row.original?.contactPerson?.lastName}
                    </>
                ),
                meta: { fieldName: contactResponseFields.CONTACT_PERSON, exportId: ['contactPerson_firstName', 'contactPerson_lastName'] },
            },
            {
                header: t('app.common.status'),
                id: contactResponseFields.CONTACT_STATUS,
                accessorFn: (row) => row.contactStatus?.name,
                enableSorting: false,
                size: w.contactStatus,
                meta: { exportId: `${contactResponseFields.CONTACT_STATUS}_name` },
            },
            {
                header: t('app.common.profileType'),
                id: contactResponseFields.LAST_CONTACT_PROFILE,
                enableSorting: false,
                size: w.lastContactProfile,
                cell: ({ row }) => (
                    <>
                        {handleActiveContactProfiles(row.original?.contactProfile ?? []).map((p) => (
                            <div key={p.id}>{p.contactProfileService.name}</div>
                        ))}
                    </>
                ),
                meta: { exportId: `${contactResponseFields.LAST_CONTACT_PROFILE}_${contactResponseFields.CONTACT_PROFILE_SERVICE}_name` },
            },
            {
                header: t('app.common.contact.profileStatus'),
                id: contactResponseFields.CONTACT_PROFILE_STATUS,
                enableSorting: false,
                size: w.contactProfileStatus,
                cell: ({ row }) => (
                    <>
                        {handleActiveContactProfiles(row.original?.contactProfile ?? []).map((p) => (
                            <div key={p.id}>{p.contactProfileStatus?.name}</div>
                        ))}
                    </>
                ),
                meta: { exportId: contactResponseFields.CONTACT_PROFILE_STATUS },
            },
            {
                header: t('app.common.city'),
                id: contactResponseFields.CITY,
                accessorKey: contactResponseFields.CITY,
                enableSorting: true,
                size: w.city,
                meta: { exportId: contactResponseFields.CITY },
            },
            {
                header: t('app.common.street'),
                id: contactResponseFields.STREET,
                accessorKey: contactResponseFields.STREET,
                enableSorting: true,
                size: w.street,
                meta: { exportId: contactResponseFields.STREET },
            },
            {
                header: t('app.common.streetNumber'),
                id: contactResponseFields.STREET_NUMBER,
                accessorKey: contactResponseFields.STREET_NUMBER,
                enableSorting: false,
                size: w.streetNumber,
                meta: { exportId: contactResponseFields.STREET_NUMBER },
            },
            {
                header: t('app.common.contactPhoneNumber1'),
                id: `${contactResponseFields.CONTACT_PHONE}1`,
                accessorFn: (row) => row.contactPhone?.[0]?.phoneNumber,
                enableSorting: false,
                size: w.contactPhone1,
                meta: { exportId: `${contactResponseFields.CONTACT_PHONE}_0_phoneNumber` },
            },
            {
                header: t('table.header.contacts.contactPhoneNumber2'),
                id: `${contactResponseFields.CONTACT_PHONE}2`,
                accessorFn: (row) => row.contactPhone?.[1]?.phoneNumber,
                enableSorting: false,
                size: w.contactPhone2,
                meta: { exportId: `${contactResponseFields.CONTACT_PHONE}_1_phoneNumber` },
            },
            {
                header: t('app.common.email.label'),
                id: contactResponseFields.CONTACT_EMAIL,
                accessorFn: (row) => row.contactEmail?.[0]?.email,
                enableSorting: false,
                size: w.contactEmail,
                meta: { exportId: `${contactResponseFields.CONTACT_EMAIL}_0_email` },
            },
            {
                header: t('table.header.contacts.previousActivityDate'),
                id: 'previousActivityDate',
                accessorFn: (row) => row.previousActivityDate,
                enableSorting: true,
                size: w.previousActivityDate,
                cell: ({ row }) => <>{formatDateTime(row.original.previousActivityDate)}</>,
                meta: { exportId: 'previousActivityDate' },
            },
            {
                header: t('table.header.contacts.nextActivityDate'),
                id: 'nextActivityDate',
                accessorFn: (row) => row.nextActivityDate,
                enableSorting: true,
                size: w.nextActivityDate,
                cell: ({ row }) => {
                    const nextActivityDate = row.original.nextActivityDate
                    const dayDiff = dayjs().diff(nextActivityDate, 'day')
                    
                    return <>{nextActivityDate && <span className={`${dayDiff > 0 && 'text-danger'}`}>{formatDateTime(nextActivityDate)}</span>}</>
                },
                meta: { exportId: 'nextActivityDate' },
            },
            {
                header: t('app.common.agent'),
                id: contactResponseFields.AGENT,
                accessorKey: contactResponseFields.AGENT,
                enableSorting: true,
                size: w.agent,
                cell: ({ row }) => (
                    <>
                        {row.original?.agent?.firstName} {row.original?.agent?.lastName}
                    </>
                ),
                meta: { fieldName: contactResponseFields.AGENT, exportId: ['agent_firstName', 'agent_lastName'] },
            },
            {
                header: t('app.common.company.label'),
                id: contactResponseFields.CONTACT_PERSON_COMPANY,
                accessorKey: contactResponseFields.CONTACT_PERSON_COMPANY,
                enableSorting: true,
                size: w.contactPersonCompany,
                cell: ({ row }) => (
                    <>
                        {row.original?.contactPersonCompany
                            ?.map(({ company }) => company?.contactCompany?.name)
                            .filter(Boolean)
                            .join(', ')}
                    </>
                ),
                meta: { exportId: ['contactPersonCompany_0_company_contactCompany_name'] },
            },
            {
                header: t('app.common.contact.establishmentDate'),
                id: contactResponseFields.INITIAL_CONTACT_DATE,
                accessorFn: (row) => row.initialContactDate,
                enableSorting: true,
                size: w.initialContactDate,
                cell: ({ row }) => formatDateTime(row.original.initialContactDate),
                meta: { exportId: contactResponseFields.INITIAL_CONTACT_DATE },
            },
            {
                header: t('table.header.contacts.priceTo'),
                id: contactResponseFields.PRICE_TO,
                accessorKey: contactResponseFields.PRICE_TO,
                enableSorting: false,
                size: w.priceTo,
                cell: ({ row }) => (
                    <>{row.original?.lastContactProfile?.priceTo ? formatNumberWithTwoDecimals(row.original.lastContactProfile.priceTo) : ''}</>
                ),
                meta: { exportId: 'lastContactProfile_priceTo' },
                printValue: (row) =>
                    row?.lastContactProfile?.priceTo ? formatNumberWithTwoDecimals(row.lastContactProfile.priceTo) : '',
            },
            {
                header: t('app.common.contact.dataSource'),
                id: contactResponseFields.DATA_SOURCE,
                accessorFn: (row) => row.dataSource?.name,
                enableSorting: true,
                size: w.dataSource,
                meta: { exportId: `${contactResponseFields.DATA_SOURCE}_name` },
            },
            {
                header: t('table.header.contacts.notes'),
                id: contactResponseFields.NOTES,
                accessorKey: `${contactResponseFields.PREVIOUS_ACTIVITY}.${contactResponseFields.NOTES}`,
                enableSorting: false,
                size: w.notes,
                cell: ({ row, table }) => (
                    row.original.previousActivity?.notes
                ),
                meta: { exportId: `${contactResponseFields.PREVIOUS_ACTIVITY}_${contactResponseFields.NOTES}` },
            },
            {
                header: t('app.common.activityType'),
                id: contactResponseFields.NEXT_ACTIVITY,
                accessorFn: (row) => row.nextActivity?.activityType?.name,
                enableSorting: false,
                size: w.nextActivity,
                meta: { exportId: `${contactResponseFields.NEXT_ACTIVITY}_activityType_name` },
            },
            {
                id: 'actions',
                enableResizing: false,
                enableSorting: false,
                size: w.actions,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => {
                    const itemAgentId = row.original.agent?.id
                    const itemAgentOfficeId = row.original.agent?.agencyOffice?.id

                    return (
                        <ul className="d-flex mb-0 p-2 gap-2 justify-content-center align-items-center">
                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={itemAgentId}
                                itemAgentOfficeId={itemAgentOfficeId}
                                module={location}
                                action={action.edit}
                            >
                                {({ hasModulePermission }: PermissionsProps) =>
                                    hasModulePermission && (
                                        <>
                                            <li className="list-inline-item edit">
                                                <Link
                                                    title={t('button.edit')}
                                                    to={`/contacts/${row.original.id}/edit`}
                                                    className="text-info d-inline-block edit-item-btn"
                                                >
                                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                                </Link>
                                            </li>
                                            <li className="list-inline-item edit">
                                                <Link
                                                    title={t('button.delete')}
                                                    to="#"
                                                    className="text-danger d-inline-block remove-item-btn"
                                                    onClick={() => handleDelete(row.original.id)}
                                                >
                                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                                </Link>
                                            </li>
                                        </>
                                    )
                                }
                            </AppPermissionsSwitcher>
                        </ul>
                    )
                },
            },
        ],
        [handleDelete, t, location, w]
    )
}

export default useTableColumns
